import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Target, Check, Clock, Plus, Trash2, Edit3, Inbox, X } from "lucide-react";

const STATUSES = [
  { value: "not_yet", label: "Not Yet", color: "#FFC107", Icon: Target },
  { value: "on_the_route", label: "On the Route", color: "#FF1E1E", Icon: Clock },
  { value: "done", label: "Done", color: "#22C55E", Icon: Check },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const EMPTY_FORM = {
  title: "",
  description: "",
  state_code: "",
  category: "",
  status: "not_yet",
};

export default function BucketListAdminCard() {
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadItems = useCallback(
    () => api.get("/bucket-list").then((r) => setItems(r.data || [])).catch(() => {}),
    []
  );
  const loadSuggestions = useCallback(
    () =>
      api
        .get("/admin/community/submissions", {
          params: { kind: "bucket_list_suggestion", status: "pending" },
        })
        .then((r) => setSuggestions(r.data || []))
        .catch(() => {}),
    []
  );

  useEffect(() => {
    loadItems();
    loadSuggestions();
  }, [loadItems, loadSuggestions]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      description: item.description || "",
      state_code: item.state_code || "",
      category: item.category || "",
      status: item.status || "not_yet",
    });
    window.scrollTo({ top: document.getElementById("bucket-admin-form")?.offsetTop || 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      state_code: form.state_code || null,
      category: form.category.trim() || null,
      status: form.status,
    };
    try {
      if (editingId) {
        await api.patch(`/admin/bucket-list/${editingId}`, payload);
        toast.success("Bucket item updated.");
      } else {
        await api.post("/admin/bucket-list", payload);
        toast.success("Added to the bucket list.");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (item, status) => {
    setBusyId(item.id);
    try {
      await api.patch(`/admin/bucket-list/${item.id}`, { status });
      await loadItems();
    } catch {
      toast.error("Status change failed.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    setBusyId(item.id);
    try {
      await api.delete(`/admin/bucket-list/${item.id}`);
      toast("Removed.");
      await loadItems();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const promote = async (sub) => {
    setBusyId(sub.id);
    try {
      await api.post(`/admin/bucket-list/promote-suggestion/${sub.id}`);
      toast.success(`Promoted "${sub.title}" to the official list.`);
      await Promise.all([loadItems(), loadSuggestions()]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Promote failed.");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (sub) => {
    if (!window.confirm(`Reject "${sub.title}"?`)) return;
    setBusyId(sub.id);
    try {
      await api.patch(`/admin/community/submissions/${sub.id}`, null, {
        params: { status: "rejected" },
      });
      toast("Rejected.");
      await loadSuggestions();
    } catch {
      toast.error("Reject failed.");
    } finally {
      setBusyId(null);
    }
  };

  const grouped = useMemo(() => {
    const g = { not_yet: [], on_the_route: [], done: [] };
    items.forEach((i) => {
      (g[i.status] || g.not_yet).push(i);
    });
    return g;
  }, [items]);

  return (
    <div className="tac-card p-7 mt-8" data-testid="bucket-list-admin-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-2">
            <Target size={12} /> Bucket List control
          </div>
          <h2 className="font-display text-3xl uppercase mt-2">BooBoo's Bucket List</h2>
        </div>
        <div className="font-mono-tactical text-xs text-white/50 uppercase tracking-[0.25em]">
          {items.length} items · {suggestions.length} pending suggestions
        </div>
      </div>

      {/* --- Create / edit form --- */}
      <form
        onSubmit={submit}
        id="bucket-admin-form"
        className="mt-6 grid grid-cols-1 md:grid-cols-6 gap-3"
        data-testid="bucket-admin-form"
      >
        <input
          type="text"
          placeholder="Title (e.g. Swim in the Great Salt Lake)"
          className="tac-input md:col-span-3"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          required
          data-testid="bucket-admin-title"
        />
        <select
          className="tac-input md:col-span-1"
          value={form.state_code}
          onChange={(e) => setForm((f) => ({ ...f, state_code: e.target.value }))}
          data-testid="bucket-admin-state"
        >
          <option value="">State (optional)</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          className="tac-input md:col-span-1"
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          data-testid="bucket-admin-status"
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Category (optional)"
          className="tac-input md:col-span-1"
          value={form.category}
          onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          data-testid="bucket-admin-category"
        />
        <textarea
          placeholder="Description (optional)"
          className="tac-input md:col-span-6"
          rows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          data-testid="bucket-admin-description"
        />
        <div className="md:col-span-6 flex flex-wrap gap-2 items-center">
          <button
            type="submit"
            disabled={saving}
            className="btn-blaze"
            data-testid="bucket-admin-save"
          >
            {editingId ? <Edit3 size={14} /> : <Plus size={14} />}
            {saving ? "SAVING..." : editingId ? "UPDATE ITEM" : "ADD TO BUCKET LIST"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="btn-ghost text-xs"
              data-testid="bucket-admin-cancel"
            >
              <X size={14} /> Cancel edit
            </button>
          )}
          <span className="font-mono-tactical text-[11px] text-white/40 uppercase tracking-widest ml-auto">
            {editingId ? `Editing #${editingId.slice(0, 6)}` : "Source: BooBoo"}
          </span>
        </div>
      </form>

      {/* --- Pending fan suggestions --- */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Inbox size={14} className="text-[#FFC107]" />
          <h3 className="font-display text-xl uppercase">
            Fan suggestions — pending ({suggestions.length})
          </h3>
        </div>
        {suggestions.length === 0 ? (
          <p className="text-xs font-mono-tactical uppercase tracking-widest text-white/40">
            Inbox empty. Fans haven't thrown anything new over the fence.
          </p>
        ) : (
          <div className="space-y-2" data-testid="bucket-admin-suggestions">
            {suggestions.map((s) => (
              <div
                key={s.id}
                className="flex flex-col md:flex-row md:items-center gap-3 border border-white/10 p-3"
                data-testid={`bucket-suggestion-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase leading-none">{s.title}</div>
                  <div className="font-mono-tactical text-[10px] uppercase tracking-widest text-white/50 mt-1">
                    By {s.submitter_name || "anon"}
                    {s.state_code ? ` · ${s.state_code}` : ""}
                  </div>
                  {s.why && (
                    <p className="text-sm text-white/70 mt-1 line-clamp-2">{s.why}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => promote(s)}
                    disabled={busyId === s.id}
                    className="btn-blaze text-xs"
                    data-testid={`bucket-suggestion-${s.id}-approve`}
                  >
                    <Check size={14} /> Approve + add
                  </button>
                  <button
                    type="button"
                    onClick={() => reject(s)}
                    disabled={busyId === s.id}
                    className="btn-ghost text-xs"
                    data-testid={`bucket-suggestion-${s.id}-reject`}
                  >
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Current items by status --- */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {STATUSES.map(({ value, label, color, Icon }) => {
          const bucket = grouped[value] || [];
          return (
            <div key={value} data-testid={`bucket-admin-column-${value}`}>
              <div
                className="flex items-center justify-between mb-2 pb-2 border-b"
                style={{ borderColor: `${color}55` }}
              >
                <div className="flex items-center gap-2" style={{ color }}>
                  <Icon size={14} />
                  <span className="font-display uppercase tracking-wider">{label}</span>
                </div>
                <span className="font-mono-tactical text-xs text-white/40">{bucket.length}</span>
              </div>
              {bucket.length === 0 ? (
                <p className="text-xs font-mono-tactical uppercase tracking-widest text-white/30 py-3">
                  —
                </p>
              ) : (
                <div className="space-y-2">
                  {bucket.map((item) => {
                    const meta = STATUS_MAP[item.status] || STATUSES[0];
                    return (
                      <div
                        key={item.id}
                        className="border border-white/10 p-3 flex flex-col gap-2"
                        data-testid={`bucket-admin-item-${item.id}`}
                      >
                        <div className="flex items-start gap-2 justify-between">
                          <div className="min-w-0">
                            <div className="font-display text-base uppercase leading-none">
                              {item.title}
                            </div>
                            <div className="font-mono-tactical text-[10px] uppercase tracking-widest text-white/40 mt-1">
                              {item.is_community_suggested ? (
                                <>FAN SUGGESTION · {item.suggested_by || "anon"}</>
                              ) : (
                                <>BOOBOO</>
                              )}
                              {item.state_code ? ` · ${item.state_code}` : ""}
                              {item.category ? ` · ${item.category}` : ""}
                            </div>
                          </div>
                          <span
                            className="font-mono-tactical text-[10px] px-1.5 py-0.5 border shrink-0"
                            style={{ borderColor: meta.color, color: meta.color }}
                          >
                            {meta.label.toUpperCase()}
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-sm text-white/70 line-clamp-2">{item.description}</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {STATUSES.filter((s) => s.value !== item.status).map((s) => (
                            <button
                              key={s.value}
                              type="button"
                              onClick={() => changeStatus(item, s.value)}
                              disabled={busyId === item.id}
                              className="text-[10px] px-2 py-1 border border-white/15 hover:border-white/40 uppercase tracking-widest font-mono-tactical"
                              style={{ color: s.color }}
                              data-testid={`bucket-admin-item-${item.id}-to-${s.value}`}
                            >
                              → {s.label}
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            disabled={busyId === item.id}
                            className="text-[10px] px-2 py-1 border border-white/15 hover:border-white/40 uppercase tracking-widest font-mono-tactical text-white/80 ml-auto"
                            data-testid={`bucket-admin-item-${item.id}-edit`}
                          >
                            <Edit3 size={10} className="inline" /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(item)}
                            disabled={busyId === item.id}
                            className="text-[10px] px-2 py-1 border border-white/15 hover:border-red-500 uppercase tracking-widest font-mono-tactical text-red-400"
                            data-testid={`bucket-admin-item-${item.id}-delete`}
                          >
                            <Trash2 size={10} className="inline" /> Del
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
